import React from 'react';
import styled, { css } from 'styled-components';
import { ButtonProps, Size, Sizes } from './types';
import { stylesOfSizes } from './styles';

const ButtonTs = ({
  variant = 'default',
  size = Sizes.NORMAL,
  type = 'button',
  offHover = false,
  children,
  buttonRef,
  ...props
}: ButtonProps) => {
  const ButtonVariant = ButtonsDict[variant];

  return (
    <ButtonVariant
      $size={size}
      $offHover={offHover}
      type={type}
      {...props}
      ref={buttonRef}
    >
      {children}
    </ButtonVariant>
  );
};

const BasePropsStyle = styled.button<{
  $size: Size;
  $offHover: boolean;
}>`
  & {
    /* CSS specificity hack. */
    cursor: pointer;
    font-family: inherit;

    ${props =>
      props.disabled &&
      `
      cursor: default;
      user-select: none;
    `}
  }
`;

const BaseStyle = styled(BasePropsStyle)`
  &:focus {
    outline: none;
  }
  border-radius: 4px;
  padding: 0 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ $size }) => stylesOfSizes[$size]}
`;

const StyledButton = styled(BaseStyle)`
  border: 1px solid #007aff;
  color: #007aff;

  ${({ disabled, $offHover }) =>
    !(disabled || $offHover) &&
    `
    :hover {
      border-color: #179fe3;
      color: #179fe3;
    }
  `}

  ${({ disabled }) =>
    disabled &&
    `
    border-color: #c2c5cc;
    color: #c2c5cc;
  `}
`;

const StyledPrimaryButton = styled(BaseStyle)`
  ${({ theme }) => theme.elements.button.primary};
  font-weight: 700;
  ${({ $offHover }) =>
    $offHover &&
    css`
      &,
      &:hover {
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
        border: solid 1px #c2c5cc;
        background: #fff;
      }
    `}
`;

const StyledFunctionalButton = styled(BasePropsStyle)`
  appearance: none;
  background-color: transparent;
  border: none;
  color: inherit;
  font: inherit;
`;

const StyledErrorButton = styled(StyledButton)`
  border-color: #d0021b;
  color: #d0021b;
  :hover {
    border-color: #d0021b;
    color: #d0021b;
  }
`;

const ButtonsDict = {
  functional: StyledFunctionalButton,
  primary: StyledPrimaryButton,
  default: StyledButton,
  error: StyledErrorButton,
};

export default ButtonTs;
